/**
 * Where a visitor came from, read off the URL they are standing on.
 *
 * The lead row has always had columns for this — `source_source`, `source_media`,
 * `source_campaign`, `source_term`, `source_refer_url` — and the config-driven form has
 * always had a `context` prop documented as "attribution carried on the first submit".
 * Nothing ever filled it in, so every config-driven lead was attributed "Direct" no matter
 * which ad it arrived from. This is that missing half.
 *
 * ─── Why it is read here and not on the server ──────────────────────────────────────
 * The UTM parameters live in the visitor's address bar. The server sees them only if the
 * client sends them, and the drop that needs them may happen minutes later in a background
 * sweep — long after the page is gone. So they are read once, at capture, and persisted on
 * the lead row; see server/migrations/20260824140000-add-source-term-to-leads.js.
 *
 * ─── The RULES are not here ─────────────────────────────────────────────────────────
 * Which parameters count, and what an absent one means, live in `./leadAttributionRules`,
 * which is a DOCUMENTED DUPLICATE of `server/lib/leadAttributionRules.js` — read the banner
 * at the top of that file before changing either. This module is only the BROWSER half: get
 * the query string, hand it to the rule, and add the two facts that are not query parameters
 * at all (`document.referrer` and the path).
 *
 * ─── Why a destination has to be passed in ──────────────────────────────────────────
 * The two CRMs read different parameters and disagree about what "no attribution" is:
 * `erp` takes the bare `?source=/media=/campaign=` triplet and falls back to the university
 * source and "Direct"; `lsq` takes only `utm_*` and stores "" — a stand-in there is a
 * fabricated campaign in LeadSquared's reporting. Reading the URL WITHOUT knowing which set
 * applies is precisely the bug this signature exists to make unrepresentable, so the
 * argument is required rather than defaulted.
 *
 * ─── The campaign belongs to the SESSION, not the page (`lsq` only, for now) ──────────
 * An ad lands the visitor on `/?utm_source=google`; they browse to `/online-mba` and drop
 * the lead there. Read off the page the form is on, that lead carried no `utm_*` — and
 * because a custom form's tuple REPLACES the stored one, the drop wiped the campaign an
 * earlier capture may already have written. So both families' parameters are recorded in
 * sessionStorage from the page they arrived on (`captureSessionAttributionParams`), and the
 * `lsq` rules read that copy. `erp` still reads the page's own URL; its set is recorded
 * anyway so switching it over later needs no new capture.
 *
 * ─── Not used by the lead-drop widgets ──────────────────────────────────────────────
 * No widget imports this file, deliberately. They keep their own pre-existing behaviour
 * through the widget branch of `buildMobileOtpLeadPayload`. Changing that is a separate
 * decision from this one.
 */

import {
  ATTRIBUTION_FAMILY_ERP,
  ATTRIBUTION_FAMILY_LSQ,
  LEAD_ATTRIBUTION_PARAM_KEYS,
  hasUrlAttribution,
  leadAttributionFamily,
  parseAttributionParams,
  resolveLeadAttribution,
  type LeadAttributionFamily,
} from "./leadAttributionRules";

export interface LeadAttribution {
  source_source: string;
  source_media: string;
  source_campaign: string;
  /** utm_term — the keyword or ad group a paid click arrived on. */
  source_term: string;
  source_refer_url: string;
  source_path: string;
  source_params: string;
  /**
   * Did THIS visit actually carry attribution parameters? For `erp` that is the page's URL;
   * for `lsq` it is the session's (see the header).
   *
   * Not the same as "the four values above are non-empty": for `erp` a clean URL still
   * resolves to the university source and "Direct"/"Direct". The server uses this to decide
   * whether a RETURNING visitor's stored attribution may be replaced — a second visit that
   * carries a campaign replaces it, an organic return leaves it alone rather than erasing
   * the campaign the first visit recorded.
   */
  from_url: boolean;
}

const EMPTY: LeadAttribution = {
  source_source: "",
  source_media: "",
  source_campaign: "",
  source_term: "",
  source_refer_url: "",
  source_path: "",
  source_params: "",
  from_url: false,
};

export interface ReadLeadAttributionOptions {
  /**
   * The form's stored `lead_destination`, straight off the published config. `null` /
   * unknown means "not chosen", which the rules treat as `erp` because that is where such
   * a form actually drops.
   */
  destination: string | null | undefined;
  /**
   * `university.source`. Used by the `erp` rules as the fallback when the URL named no
   * source, and IGNORED entirely by `lsq` — that is the whole point of the split.
   */
  universitySource?: string | null;
}

const SESSION_PARAMS_KEY = "entry_attribution_params";

/** Each family's attribution parameters, as this tab's session last saw them on a URL. */
export type SessionAttributionParams = Partial<
  Record<LeadAttributionFamily, Record<string, string>>
>;

const FAMILIES: readonly LeadAttributionFamily[] = [
  ATTRIBUTION_FAMILY_LSQ,
  ATTRIBUTION_FAMILY_ERP,
];

/** `family`'s non-empty parameters on `params`, and nothing else from the query string. */
function familyParams(
  family: LeadAttributionFamily,
  params: unknown,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!params || typeof params !== "object") return out;
  const source = params as Record<string, unknown>;
  for (const keys of Object.values(LEAD_ATTRIBUTION_PARAM_KEYS[family])) {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === "string" && value.trim()) out[key] = value.trim();
    }
  }
  return out;
}

/**
 * Fold one URL's parameters into the session's sets. Pure, so it is testable without a
 * browser.
 *
 * Per family, ALL OR NOTHING. A URL carrying any of a family's parameters REPLACES that
 * family's set whole — a second ad click in the same tab is the newer campaign, and mixing
 * `utm_source` from one click with `utm_campaign` from another would invent a campaign that
 * never ran (the server treats the columns as one tuple for the same reason). A URL carrying
 * none of them leaves the set alone: that is the navigation this exists to survive. The
 * families are independent, so a bare `?media=` link does not erase the session's `utm_*`.
 *
 * `stored` is re-filtered through the family keys, so a malformed or hand-edited entry can
 * only ever contribute real attribution parameters.
 */
export function mergeSessionAttributionParams(
  stored: unknown,
  urlParams: Record<string, string> | null | undefined,
): SessionAttributionParams {
  const prior =
    stored && typeof stored === "object" ? (stored as Record<string, unknown>) : {};
  const next: SessionAttributionParams = {};
  for (const family of FAMILIES) {
    const fromUrl = familyParams(family, urlParams);
    next[family] = Object.keys(fromUrl).length ? fromUrl : familyParams(family, prior[family]);
  }
  return next;
}

/**
 * Record the current page's attribution parameters for this tab's session, and return the
 * session's sets.
 *
 * `Providers` calls it on the first mount, while the browser is still on the page the
 * visitor arrived on — a popup form is not mounted until it opens, so waiting for a form
 * would miss a campaign page that had none. `readLeadAttribution` calls it too: an inline
 * form renders before `Providers`' effect runs, and a page reached later may carry a newer
 * campaign. `null` during SSR or without storage, and the caller falls back to the page's
 * own URL — all it could ever read without storage.
 */
export function captureSessionAttributionParams(): SessionAttributionParams | null {
  if (typeof window === "undefined") return null;
  try {
    let stored: unknown = null;
    try {
      stored = JSON.parse(sessionStorage.getItem(SESSION_PARAMS_KEY) || "null");
    } catch {
      /* unparseable — start again from this page rather than drop its parameters */
    }
    const next = mergeSessionAttributionParams(
      stored,
      parseAttributionParams(window.location.search),
    );
    sessionStorage.setItem(SESSION_PARAMS_KEY, JSON.stringify(next));
    return next;
  } catch {
    return null;
  }
}

/**
 * Read this visit's attribution under one destination's rules.
 *
 * `lsq` reads the session's `utm_*` (see the header); `erp` reads the current page's URL.
 *
 * Returns all-empty during SSR and on any failure — a malformed URL must not stop a form
 * rendering, and an unattributed lead is worth infinitely more than no lead. Note that
 * all-empty is also the correct `lsq` answer for a clean URL, so the two are deliberately
 * spelled the same.
 */
export function readLeadAttribution(
  options: ReadLeadAttributionOptions,
): LeadAttribution {
  if (typeof window === "undefined") return { ...EMPTY };
  try {
    const destination = options?.destination ?? null;
    // Recorded for both families on every read; only `lsq` reads the session copy back.
    const session = captureSessionAttributionParams();
    const params =
      session && leadAttributionFamily(destination) === ATTRIBUTION_FAMILY_LSQ
        ? (session[ATTRIBUTION_FAMILY_LSQ] ?? {})
        : parseAttributionParams(window.location.search);
    const resolved = resolveLeadAttribution({
      destination,
      params,
      universitySource: options?.universitySource ?? "",
    });
    return {
      ...resolved,
      from_url: hasUrlAttribution(destination, params),
      // The page that referred them here, which is a different fact from the page they are
      // on — the CRM wants both. Neither is a query parameter, so neither is the rule
      // module's business and both are read the same way for every destination.
      source_refer_url: document.referrer || "",
      source_path: window.location.origin + window.location.pathname,
      source_params: window.location.search.replace(/^\?/, ""),
    };
  } catch {
    return { ...EMPTY };
  }
}
